import {
  useEffect, useState, useMemo, useContext,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  formatDate,
} from '#common/utils';
import {
  useGetInventoryReportMutation,
} from '#services/inventory';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { EnhancedTableHeader, EnhancedTableProps } from '#components/EnhancedTable';

import InventoryReportTable from './ReportTable';

const PAGE_SIZE = 30;

// Key under which the collection filter (the *deselected* collection ids) is
// stored so it survives leaving the page and coming back. sessionStorage is
// used rather than the URL because the mobile layout reloads the whole page on
// any URL/search change, and rather than component state because that reload —
// and a plain remount on browser-back — would otherwise reset the filter.
const HIDDEN_COLLECTIONS_KEY = 'inventoryReportHiddenCollections';

function readHiddenCollections(): Set<string> {
  try {
    const raw = sessionStorage.getItem(HIDDEN_COLLECTIONS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeHiddenCollections(hidden: Set<string>) {
  try {
    sessionStorage.setItem(HIDDEN_COLLECTIONS_KEY, JSON.stringify([...hidden]));
  } catch {
    // ignore storage failures (e.g. private mode) — filter just won't persist
  }
}

type ReportTrigger = (arg: Record<string, unknown>) => Promise<{ data?: unknown; error?: unknown }>;

interface ReportCollection {
  id: string;
  name: string;
  inactivated?: boolean;
}

interface ReportRowDetail {
  availabilityStatus?: string;
  productName?: string;
  productInactivated?: boolean;
  availableDate?: string;
  color?: string;
  productId?: string | number;
  sizes: Record<string, number>;
  [key: string]: unknown;
}

interface ReportData {
  collectionId: string;
  rows: Array<Record<string, unknown>>;
  total: number;
  allSizes: Array<string | number>;
  [key: string]: unknown;
}

export default function InventoryReport() {
  const userInfo = useSelector((state: { auth: { userInfo: { isCustomer?: boolean } } }) => state.auth.userInfo);
  const { t } = useTranslation('inventory');
  const [getReportRaw] = useGetInventoryReportMutation();
  const getReport = getReportRaw as unknown as ReportTrigger;
  const [collections, setCollections] = useState<ReportCollection[]>([]);

  const [reports, setReports] = useState<Record<string, ReportData>>({});
  const navigate = useNavigate();

  // Deselected collection ids, seeded from sessionStorage so the filter is
  // restored after drilling into a style detail and coming back.
  const [hiddenCollections, setHiddenCollections] = useState<Set<string>>(readHiddenCollections);

  const toggleCollection = (id: string, selected: boolean) => {
    setHiddenCollections((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.delete(id);
      } else {
        next.add(id);
      }
      writeHiddenCollections(next);
      return next;
    });
  };
  const isMobile = useMediaQuery('(max-width:600px)', { noSsr: true });
  const useMobileLayout = isMobile && !!userInfo?.isCustomer;

  const {
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const queryOptions = {
    pageSize: PAGE_SIZE,
  };

  useEffect(() => {
    if (!userInfo?.isCustomer) return undefined;
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute('content', 'width=device-width, initial-scale=1.0');
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'viewport';
      newMeta.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(newMeta);
    }
    return () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.remove();
      }
    };
  }, [userInfo?.isCustomer]);

  useEffect(() => {
    void getReport({
      ...queryOptions,

      // showInProduction by default
      showInProduction: true,
    }).then((res) => {
      const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
      if (hasError) {
        return;
      }

      const {
        categories,
        reports: reportList,
      } = (res as { data: { categories: { collections: ReportCollection[] }; reports: ReportData[] } }).data;

      setCollections(categories.collections);

      const reportsMap = reportList.reduce<Record<string, ReportData>>((acc, report) => {
        acc[report.collectionId] = report;
        return acc;
      }, {});
      setReports(reportsMap);
    });
  }, []);

  const handleOrdersExport = () => {
    const exportUrl = '/api/export/inventoryReports';
    window.open(exportUrl, '_blank');
  };

  return (
    useMemo(() => (
      <>
        {userInfo.isCustomer && (
        <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
          <Typography variant={'h7' as TypographyProps['variant']} sx={{ fontWeight: 'fontWeightBold' as unknown as number }}>
            {t('inquiryTitle')}
          </Typography>
        </Box>
        )}

        { !userInfo.isCustomer && (
          <>
            <Box sx={{ typography: 'h7' }}>
              {t('reportTitle')}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                bgcolor: 'background.paper',
                borderRadius: 1,
              }}
            >
              <Box />
              <ButtonGroup variant="outlined">
                <Button
                  onClick={handleOrdersExport}
                >
                  {t('export')}
                </Button>
              </ButtonGroup>
            </Box>
          </>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {
            collections.map((c, idx) => (
              <FormControlLabel
                key={idx}
                control={(
                  <Checkbox
                    checked={!hiddenCollections.has(c.id)}
                    onChange={(e, selected) => toggleCollection(c.id, selected)}
                  />
                )}
                label={c.name}
              />
            ))
          }
        </Box>

        {
          collections.map((collection) => {
            if (hiddenCollections.has(collection.id)) {
              return null;
            }

            const report = reports[collection.id];
            if (!report) {
              return null;
            }

            const styleHeader: EnhancedTableHeader = {
              id: 'style',
              label: t('table.style'),
              ...(useMobileLayout && {
                sx: {
                  maxWidth: 170,
                  minWidth: 140,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                },
              }),
              renderer: (row) => {
                const {
                  availabilityStatus,
                  productName,
                  productInactivated,
                  availableDate,
                  color,
                  productId,
                } = row as unknown as ReportRowDetail;
                let datePart;
                switch (availabilityStatus) {
                  case 'inStock':
                    datePart = t('status.inStock');
                    break;
                  case 'pastDue':
                    datePart = t('status.pastDue', { date: formatDate(availableDate as string) });
                    break;
                  case 'pending':
                    datePart = t('status.noAvailableDate');
                    break;
                  default:
                    datePart = formatDate(availableDate as string);
                }
                const url = userInfo.isCustomer
                  ? `/shop/add?productId=${productId}`
                  : `/shop/add?productId=${productId}&inventory=true`;

                const colorLine = useMobileLayout && color && (
                  <Box sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.25 }}>
                    {color}
                  </Box>
                );

                if (productInactivated) {
                  return (
                    <Box>
                      <Tooltip title={t('inactiveTooltip')}>
                        <span>
                          <Link
                            className="disabled-link"
                            sx={{
                              ml: 0.5,
                              textDecoration: 'inherit',
                            }}
                          >
                            {t('styleLinkInactive', { name: productName, datePart })}
                          </Link>
                        </span>
                      </Tooltip>
                      {colorLine}
                    </Box>
                  );
                }

                return (
                  <Box>
                    <Link
                      href={url}
                      onClick={(e) => {
                        e.preventDefault();
                        void navigate(url);
                      }}
                      sx={{
                        ml: 0.5,
                        textDecoration: 'inherit',
                      }}
                    >
                      {t('styleLink', { name: productName, datePart })}
                    </Link>
                    {colorLine}
                  </Box>
                );
              },
            };

            const tableHeaders: EnhancedTableProps['headers'] = [styleHeader];
            if (!useMobileLayout) {
              tableHeaders.push({
                id: 'color',
                label: t('table.color'),
                renderer: (row) => (row as unknown as ReportRowDetail).color,
              });
            }

            // build size headers
            report.allSizes.forEach((s) => {
              tableHeaders.push({
                id: `size|${s}`,
                label: t('table.size', { size: s }),
                renderer: (row) => {
                  let amounts: string | number | undefined = (row as unknown as ReportRowDetail).sizes[s];
                  if (amounts === undefined) {
                    amounts = '-';
                  } else if (amounts === 0) {
                    amounts = '';
                  }

                  return amounts;
                },
              });
            });

            const tableProps = {
              name: 'inventory-report-table',
              headers: tableHeaders,
              pinnedColumns: {
                left: useMobileLayout ? ['style'] : ['style', 'color'],
              },
              enableSort: true,
              pageSize: PAGE_SIZE,
            };

            return (
              <InventoryReportTable
                key={collection.id}
                collection={collection}
                report={report}
                tableProps={tableProps}
                getReport={getReport}
                queryOptions={queryOptions}
              />
            );
          })
        }
      </>
    ), [
      collections,
      reports,
      hiddenCollections,
      useMobileLayout,
      t,
    ])
  );
}
